import { Nullable } from '~/types/Nullable';

import { PromptModelTestCase } from '../types/deprecated';

// Filters newTestCases that are rated, and also not in the oldTestCases (keyed on entity.id)
export const getNewRatedTestCases = (
  oldTestCases: PromptModelTestCase[],
  newTestCases: PromptModelTestCase[],
) => {
  const testIsTestCaseRated = ({ outcome, goldValue }: PromptModelTestCase) =>
    outcome === 'correct' ||
    (goldValue && (outcome === 'partially_correct' || outcome === 'incorrect'));

  // The unique key for a test case is composite on entity.id + entity.type
  const keyTestCase = ({ entity }: PromptModelTestCase) =>
    `${entity.type}-${entity.id}`;

  const oldRatedTestCasesMap = Object.fromEntries(
    oldTestCases
      .filter(testIsTestCaseRated)
      .map((testCase) => [keyTestCase(testCase), testCase]),
  );

  return newTestCases.filter(
    (testCase) =>
      testIsTestCaseRated(testCase) &&
      !oldRatedTestCasesMap[keyTestCase(testCase)],
  );
};

export const testIsTestCaseEvaluating = ({ state }: PromptModelTestCase) =>
  state === 'not_started' || state === 'queued' || state === 'running';

export const isTestCaseRated = (
  testCase: PromptModelTestCase | undefined,
): boolean => {
  return Boolean(
    testCase && testCase.outcome && testCase.outcome !== 'unknown',
  );
};

export const findTestCaseByNumber = (
  testCases: PromptModelTestCase[] | undefined,
  testCaseNumber: number,
): PromptModelTestCase | undefined => {
  return testCases?.find((tc) => tc.number === testCaseNumber);
};

export const findTestCaseIndexByNumber = (
  testCases: PromptModelTestCase[] | undefined,
  testCaseNumber: number,
): number => {
  return testCases?.findIndex((tc) => tc.number === testCaseNumber) ?? -1;
};

export const requireNextDocumentAction = (
  testCase: PromptModelTestCase | undefined,
): boolean =>
  testCase?.outcome === 'incorrect' ||
  testCase?.outcome === 'partially_correct';

export const getVisibleTestCaseCount = (
  testCases: PromptModelTestCase[],
  activeTestCaseNumber: number,
  furthestNavigated: number,
): number => {
  const currentTestCase =
    testCases[Math.min(activeTestCaseNumber, testCases.length) - 1];

  if (
    furthestNavigated <= activeTestCaseNumber &&
    requireNextDocumentAction(currentTestCase)
  ) {
    return currentTestCase!.number;
  }

  const ratedCount = testCases.filter((tc) => isTestCaseRated(tc)).length;
  return Math.min(ratedCount + 1, testCases.length);
};

export const getNextActiveTestCase = (
  testCases: PromptModelTestCase[],
): Nullable<PromptModelTestCase> => {
  if (testCases.length === 0) return null;

  const lastTestCase = testCases[testCases.length - 1];
  if (lastTestCase.outcome !== 'unknown') return lastTestCase;

  const firstUnreviewedTestCase = testCases.find(
    (tc) => tc.outcome === 'unknown',
  );

  return firstUnreviewedTestCase ?? lastTestCase;
};

export const extractDocumentVersionIds = (
  testCases: PromptModelTestCase[],
): number[] => {
  return testCases
    .filter((tc) => tc?.entity?.id)
    .map((tc) => Number(tc.entity.id));
};

export const mergePollTestCasesIntoCurrent = (
  currentTestCases: PromptModelTestCase[],
  incomingTestCases: PromptModelTestCase[],
): PromptModelTestCase[] => {
  const currentList = currentTestCases ?? [];
  const incomingList = incomingTestCases ?? [];

  const incomingTestCasesByNumberMap = new Map(
    incomingList.map((tc) => [tc.number, tc]),
  );
  const currentTestCaseNumbersSet = new Set(currentList.map((tc) => tc.number));

  const mergeCurrentCaseWithIncoming = (
    currentCase: PromptModelTestCase,
  ): PromptModelTestCase => {
    const incomingCaseFromPoll = incomingTestCasesByNumberMap.get(
      currentCase.number,
    );

    // No update for this case — keep it as-is
    if (!incomingCaseFromPoll) return currentCase;

    // The user provided feedback but the poll hasn't caught up yet
    // apply server side progress like state and modelValue while preserving the changes that user applied.
    const userRatedButStaledPollResponseFromServer =
      isTestCaseRated(currentCase) && !isTestCaseRated(incomingCaseFromPoll);

    if (userRatedButStaledPollResponseFromServer) {
      return {
        ...incomingCaseFromPoll,
        outcome: currentCase.outcome,
        goldValue: currentCase.goldValue,
      };
    }

    return incomingCaseFromPoll;
  };

  const updatedTestCases = currentList.map(mergeCurrentCaseWithIncoming);
  const newlyAddedCases = incomingList.filter(
    (tc) => !currentTestCaseNumbersSet.has(tc.number),
  );

  return [...updatedTestCases, ...newlyAddedCases];
};
